import type {
  ClozeBlank,
  DeckData,
  FlashCard,
  GameData,
  GameMode,
  MCQuestion,
  MCQOption,
  RemediationTarget,
} from '../types/deck';

const OPTION_MAX_LENGTH = 160;
const QUESTION_MAX_LENGTH = 220;

export class DeckService {
  private deck: DeckData | null = null;
  private cardMap: Map<string, FlashCard> = new Map();
  private rootCards: FlashCard[] = [];
  private recentCardIds: string[] = [];
  private readonly recentLimit = 5;

  /**
   * Load deck from pre-loaded Phaser cache data or fetch as fallback
   * @param cachedData - Optional deck data from Phaser's JSON cache
   */
  async loadDeck(cachedData?: DeckData): Promise<void> {
    if (cachedData) {
      // Use pre-loaded data from Phaser cache (more reliable)
      this.deck = cachedData;
    } else {
      // Fallback: fetch with absolute path for consistent resolution
      const response = await fetch('/data/flashcards.json');
      if (!response.ok) {
        throw new Error(`Failed to load deck: ${response.status}`);
      }
      this.deck = await response.json();
    }
    this.indexCards();
  }

  private indexCards(): void {
    if (!this.deck) return;

    this.cardMap.clear();
    this.rootCards = [];

    for (const card of this.deck.cards) {
      this.cardMap.set(card.id, card);

      // Root cards are parents or solo cards (no parentId)
      if (card.parentId === null) {
        this.rootCards.push(card);
      }
    }

    // Sort root cards by seq for stable numbering
    this.rootCards.sort((a, b) => a.seq - b.seq);
  }

  getCardById(id: string): FlashCard | undefined {
    return this.cardMap.get(id);
  }

  getRootCards(): FlashCard[] {
    return this.rootCards;
  }

  getChildCards(parentId: string): FlashCard[] {
    const parent = this.cardMap.get(parentId);
    if (!parent || !parent.childIds) return [];

    return parent.childIds
      .map(id => this.cardMap.get(id))
      .filter((card): card is FlashCard => card !== undefined);
  }

  getCardChain(rootId: string): FlashCard[] {
    const root = this.cardMap.get(rootId);
    if (!root) return [];

    const chain: FlashCard[] = [root];
    const children = this.getChildCards(rootId);
    chain.push(...children);

    return chain;
  }

  /**
   * For Milestone 1: Pick the first root card deterministically
   */
  pickFirstRootId(): string | null {
    if (this.rootCards.length === 0) return null;
    return this.rootCards[0].id;
  }

  /**
   * Get stable display number for a root card (based on seq)
   */
  getDisplayNumber(card: FlashCard): string {
    if (card.seq !== undefined && card.seq !== null) {
      return String(card.seq);
    }
    // Fallback: extract number from ID suffix
    const match = card.id.match(/-(\d+)$/);
    return match ? match[1] : card.id;
  }

  /**
   * Generate a game-ready question. Prefers card.game, falls back to derived MCQ.
   */
  generateQuestion(card: FlashCard): MCQuestion {
    const mode = this.pickMode(card);
    const game = card.game || null;

    if (mode === 'cloze') {
      const cloze = this.generateCloze(card, game);
      if (cloze) return cloze;
    }

    if (mode === 'classification') {
      return this.generateChoiceQuestion(card, game, 'classification');
    }

    // "free" is not yet supported in this client; degrade to MCQ.
    return this.generateChoiceQuestion(card, game, 'mcq');
  }

  /**
   * Legacy alias for MCQ consumers.
   */
  generateMCQ(card: FlashCard): MCQuestion {
    return this.generateQuestion(card);
  }

  private pickMode(card: FlashCard): GameMode {
    const gameMode = card.game?.mode;
    if (gameMode === 'cloze' && (card.game?.clozeLite || card.clozeLite || card.cloze)) {
      return 'cloze';
    }
    if (gameMode === 'classification') return 'classification';
    if (gameMode === 'free') return 'free';
    if (card.cardRole === 'parent') return 'classification';
    if (card.clozeLite) return 'cloze';
    return 'mcq';
  }

  private generateChoiceQuestion(card: FlashCard, game: GameData | null, mode: GameMode): MCQuestion {
    const resolvedMode: GameMode = mode === 'free' ? 'mcq' : mode;
    const seen = new Set<string>();
    const options: MCQOption[] = [];
    const stem = this.truncateForQuestion(
      game?.scenario || game?.stem || card.frontPrompt || card.front
    );
    const question = stem;

    const correctAnswer =
      (game?.choices && typeof game.answerIndex === 'number'
        ? game.choices[game.answerIndex]
        : null) ||
      card.mediumContent ||
      card.hardContent ||
      card.backPlain;

    const correctId = `opt-${card.id}-correct`;

    const pushOption = (id: string, text: string, isCorrect: boolean) => {
      const cleaned = this.cleanText(text);
      const normalized = cleaned.toLowerCase();
      if (!cleaned || seen.has(normalized) || normalized === this.cleanText(question).toLowerCase()) {
        return;
      }
      options.push({
        id,
        text: this.truncateForOption(cleaned),
        isCorrect,
      });
      seen.add(normalized);
    };

    pushOption(correctId, correctAnswer, true);

    // Add distractors from explicit choices if present.
    if (game?.choices) {
      game.choices.forEach((choice, idx) => {
        if (idx === game.answerIndex) return;
        pushOption(`opt-${card.id}-choice-${idx}`, choice, false);
      });
    }

    // Add confusable-based distractors.
    for (const distractorId of card.confusableWith || []) {
      if (options.length >= 4) break;
      const distractorCard = this.cardMap.get(distractorId);
      if (distractorCard) {
        const distractorText =
          distractorCard.mediumContent || distractorCard.hardContent || distractorCard.backPlain;
        pushOption(`opt-${distractorId}`, distractorText, false);
      }
    }

    // If still short, pull nearby subject cards as distractors.
    if (options.length < 4) {
      const sameSubject = this.rootCards
        .filter(c => c.subject === card.subject && c.id !== card.id)
        .slice(0, 6);
      for (const c of sameSubject) {
        if (options.length >= 4) break;
        pushOption(
          `opt-${c.id}`,
          c.mediumContent || c.easyContent || c.backPlain || c.front,
          false
        );
      }
    }

    // Pad with generic distractors to reach at least four.
    while (options.length < 4) {
      pushOption(`opt-generic-${options.length}`, `[Not this one ${options.length + 1}]`, false);
    }

    this.shuffleArray(options);

    return {
      card,
      mode: resolvedMode,
      question,
      stem,
      options,
      correctOptionId: correctId,
      hint: game?.hint || undefined,
      explain: game?.explain || card.easyContent || undefined,
      remediation: this.buildRemediation(card),
    };
  }

  private generateCloze(card: FlashCard, game: GameData | null): MCQuestion | null {
    const template = game?.clozeLite || card.clozeLite || card.cloze;
    if (!template) return null;

    const parsed = this.parseClozeTemplate(template);
    if (parsed.blanks.length === 0) return null;

    const seenPerBlank = new Map<string, Set<string>>();
    const blanks: ClozeBlank[] = parsed.blanks.map((blank, index) => {
      const seen = new Set<string>();
      seenPerBlank.set(blank.id, seen);

      const options: MCQOption[] = [];
      const correctOptionId = `blank-${card.id}-${index}-correct`;

      this.pushOptionForList(options, seen, correctOptionId, blank.answer, true, 90);

      const distractors = this.buildClozeDistractors(card, blank.answer, parsed.answers);
      distractors.forEach((distractor, idx) => {
        this.pushOptionForList(
          options,
          seen,
          `blank-${card.id}-${index}-d-${idx}`,
          distractor,
          false,
          90
        );
      });

      while (options.length < 4) {
        this.pushOptionForList(
          options,
          seen,
          `blank-${card.id}-${index}-fill-${options.length}`,
          `[Not ${blank.answer}]`,
          false,
          90
        );
      }

      this.shuffleArray(options);

      return {
        id: blank.id,
        label: `Blank ${index + 1}`,
        display: blank.display,
        answer: blank.answer,
        options,
        correctOptionId,
      };
    });

    return {
      card,
      mode: 'cloze',
      question: this.truncateForQuestion(parsed.prompt),
      stem: this.truncateForQuestion(game?.stem || card.frontPrompt || card.front),
      scenario: game?.scenario,
      options: blanks.flatMap(b => b.options),
      blanks,
      correctOptionIds: blanks.map(b => b.correctOptionId),
      hint: game?.hint || undefined,
      explain: game?.explain || card.easyContent || undefined,
      remediation: this.buildRemediation(card),
    };
  }

  private parseClozeTemplate(template: string): {
    prompt: string;
    blanks: { id: string; display: string; answer: string }[];
    answers: string[];
  } {
    const answers: string[] = [];
    const blanks: { id: string; display: string; answer: string }[] = [];
    let index = 1;

    const prompt = template.replace(/{{c\d+::(.*?)}}/gi, (_match, answer) => {
      const cleaned = this.cleanText(answer);
      answers.push(cleaned);
      const placeholder = `[${index}]`;
      blanks.push({ id: `c${index}`, display: placeholder, answer: cleaned });
      index++;
      return placeholder;
    });

    return {
      prompt: this.cleanText(prompt),
      blanks,
      answers,
    };
  }

  private buildClozeDistractors(card: FlashCard, answer: string, siblingAnswers: string[]): string[] {
    const pool = new Set<string>();
    siblingAnswers.forEach(a => {
      if (a && a !== answer) pool.add(a);
    });

    // Confusable cards
    for (const id of card.confusableWith || []) {
      const confusable = this.cardMap.get(id);
      if (!confusable) continue;
      const template = confusable.clozeLite || confusable.cloze;
      if (template) {
        const parsed = this.parseClozeTemplate(template);
        parsed.answers.forEach(ans => {
          if (ans !== answer) pool.add(ans);
        });
      } else if (confusable.mediumContent) {
        pool.add(confusable.mediumContent.split('.')[0]);
      }
      if (pool.size >= 6) break;
    }

    // Same-subject fallback
    if (pool.size < 4) {
      for (const other of this.rootCards) {
        if (other.id === card.id || other.subject !== card.subject) continue;
        if (other.clozeLite) {
          const parsed = this.parseClozeTemplate(other.clozeLite);
          parsed.answers.forEach(ans => {
            if (ans !== answer) pool.add(ans);
          });
        }
        if (pool.size >= 6) break;
      }
    }

    return Array.from(pool)
      .filter(text => !!text && text !== answer)
      .slice(0, 3)
      .map(text => this.truncateForOption(text, 90));
  }

  private pushOptionForList(
    list: MCQOption[],
    seen: Set<string>,
    id: string,
    text: string,
    isCorrect: boolean,
    maxLength: number = OPTION_MAX_LENGTH
  ): void {
    const cleaned = this.cleanText(text);
    if (!cleaned) return;
    const normalized = cleaned.toLowerCase();
    if (seen.has(normalized)) return;
    list.push({
      id,
      text: this.truncateForOption(cleaned, maxLength),
      isCorrect,
    });
    seen.add(normalized);
  }

  private truncateForOption(text: string, maxLength: number = OPTION_MAX_LENGTH): string {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength - 3) + '...';
  }

  private truncateForQuestion(text: string, maxLength: number = QUESTION_MAX_LENGTH): string {
    const cleaned = this.cleanText(text);
    if (cleaned.length <= maxLength) return cleaned;
    return cleaned.substring(0, maxLength - 3) + '...';
  }

  private cleanText(text: string): string {
    return (text || '').replace(/\s+/g, ' ').trim();
  }

  private buildRemediation(card: FlashCard): RemediationTarget[] {
    const remediation: RemediationTarget[] = [];
    for (const id of card.confusableWith || []) {
      const target = this.cardMap.get(id);
      if (!target) continue;
      remediation.push({
        id: target.id,
        topic: target.topic || target.front || target.rawHeader,
        subject: target.subject,
        reason: 'Commonly confused within this subject',
      });
      if (remediation.length >= 3) break;
    }
    return remediation;
  }

  private shuffleArray<T>(array: T[]): void {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
  }

  getDeck(): DeckData | null {
    return this.deck;
  }

  getTotalCards(): number {
    return this.deck?.totalCards ?? 0;
  }

  getSubjects(): string[] {
    return this.deck?.subjects ?? [];
  }

  /**
   * Get a random card, optionally filtered by subject
   */
  getRandomCard(subject?: string): FlashCard | undefined {
    if (!this.deck) return undefined;

    let cards = this.rootCards;

    // Filter by subject if provided
    if (subject) {
      cards = cards.filter(card => card.subject === subject);
    }

    if (cards.length === 0) return undefined;

    // Avoid the most recent N cards if possible
    const eligible = cards.filter(card => !this.recentCardIds.includes(card.id));
    const pool = eligible.length > 0 ? eligible : cards;

    const randomIndex = Math.floor(Math.random() * pool.length);
    const chosen = pool[randomIndex];
    this.trackRecent(chosen.id);
    return chosen;
  }

  /**
   * Get all cards for a subject
   */
  getCardsBySubject(subject: string): FlashCard[] {
    return this.rootCards.filter(card => card.subject === subject);
  }

  private trackRecent(cardId: string): void {
    this.recentCardIds = [cardId, ...this.recentCardIds.filter(id => id !== cardId)].slice(
      0,
      this.recentLimit
    );
  }
}

// Singleton instance
export const deckService = new DeckService();
